// Cobro del wizard en 3 renglones fijos (efectivo, tarjeta, transferencia) con candado y «todo aquí».
// Es solo la captura: el body sale de `toSalePayments` con los drafts de `rowsToDrafts`.
import type { PaymentLine } from '@/shared/lib/format/salePaymentLines';
import {
    MAX_PAYMENTS,
    balanceBlocker,
    createPaymentDraft,
    paymentsBlocker,
    type PaymentDraft,
    type PaymentsBalance,
} from './salePayments';
import type { RegisteredPayment } from './types';

export type RowKey = 'cash' | 'card' | 'spei';

const ROW_KEYS: readonly RowKey[] = ['cash', 'card', 'spei'];

/** Una tarjeta del renglón Tarjeta; el cliente puede pagar con varias. */
export interface CardEntry {
    id: string;
    amountCents: number;
    cardLast4: string;
    authCode: string;
    /** Solo cuenta para el reparto entre tarjetas. */
    locked: boolean;
}

export interface FixedPaymentRows {
    cash: { amountCents: number; cashOk: boolean; locked: boolean };
    /** `amountCents` es la suma de `cards`. */
    card: { amountCents: number; locked: boolean; cards: CardEntry[] };
    spei: { amountCents: number; speiKey: string; senderClabe: string; locked: boolean };
}

function newCard(id: string, amountCents = 0): CardEntry {
    return { id, amountCents, cardLast4: '', authCode: '', locked: false };
}

/** Todo en efectivo, sin candados. */
export function initialSplits(totalCents: number): FixedPaymentRows {
    return {
        cash: { amountCents: totalCents, cashOk: false, locked: false },
        card: { amountCents: 0, locked: false, cards: [newCard('card-1')] },
        spei: { amountCents: 0, speiKey: '', senderClabe: '', locked: false },
    };
}

function cardsSum(cards: readonly CardEntry[]): number {
    return cards.reduce((sum, c) => sum + c.amountCents, 0);
}

export function rowsSumCents(rows: FixedPaymentRows): number {
    return rows.cash.amountCents + rows.card.amountCents + rows.spei.amountCents;
}

/** Cambió el total a cobrar: el efectivo toma lo que no cubren tarjeta y transferencia. */
export function syncRowsTotal(rows: FixedPaymentRows, totalCents: number): FixedPaymentRows {
    const cash = Math.max(0, totalCents - rows.card.amountCents - rows.spei.amountCents);
    if (cash === rows.cash.amountCents) return rows;
    return { ...rows, cash: { ...rows.cash, amountCents: cash, cashOk: false } };
}

/** El efectivo cambió sin que el vendedor lo tecleara: la confirmación de caja ya no vale. */
function resetCashOkIfMoved(before: FixedPaymentRows, after: FixedPaymentRows): FixedPaymentRows {
    if (after.cash.amountCents === before.cash.amountCents || !after.cash.cashOk) return after;
    return { ...after, cash: { ...after.cash, cashOk: false } };
}

/** Si el vendedor tecleó el efectivo, su confirmación sigue; si se movió por reparto, se pierde. */
function keepCashOkFor(
    key: RowKey,
    before: FixedPaymentRows,
    after: FixedPaymentRows,
): FixedPaymentRows {
    return key === 'cash' ? after : resetCashOkIfMoved(before, after);
}

/** Renglón que puede absorber reparto: libre y, si es Tarjeta, con una sola tarjeta. */
function canAbsorb(rows: FixedPaymentRows, key: RowKey): boolean {
    return !rows[key].locked && (key !== 'card' || rows.card.cards.length === 1);
}

/** A dónde va el monto liberado: al efectivo si está libre; si no, a otro libre que ya tenga monto. */
function releaseTarget(rows: FixedPaymentRows, changed: RowKey): RowKey | null {
    if (changed !== 'cash' && canAbsorb(rows, 'cash')) return 'cash';
    return (
        ROW_KEYS.find(
            (k) => k !== changed && k !== 'cash' && canAbsorb(rows, k) && rows[k].amountCents > 0,
        ) ?? null
    );
}

/** El renglón tiene monto o de dónde tomarlo: lo que falta asignar o un libre con saldo. */
export function canTakeAmount(rows: FixedPaymentRows, key: RowKey, totalCents: number): boolean {
    if (rows[key].amountCents > 0 || rowsSumCents(rows) < totalCents) return true;
    return ROW_KEYS.some((k) => k !== key && canAbsorb(rows, k) && rows[k].amountCents > 0);
}

/** Tarjetas que caben: el cobro lleva máximo 3 pagos y efectivo y transferencia cuentan uno cada uno. */
export function maxCards(rows: FixedPaymentRows): number {
    const others = (rows.cash.amountCents > 0 ? 1 : 0) + (rows.spei.amountCents > 0 ? 1 : 0);
    return Math.max(1, MAX_PAYMENTS - others);
}

/** Reparte `delta` (positivo = quitar) entre dos montos libres, mitad y mitad sin bajar de 0. */
function splitDelta(a: number, b: number, delta: number): [number, number] {
    const half = Math.floor(Math.abs(delta) / 2) * Math.sign(delta);
    let nextA = a - half;
    let nextB = b - (delta - half);
    if (nextA < 0) {
        nextB += nextA;
        nextA = 0;
    }
    if (nextB < 0) {
        nextA = Math.max(0, nextA + nextB);
        nextB = 0;
    }
    return [nextA, nextB];
}

/**
 * Fija el monto de un renglón y los otros cuadran el total. Lo que sube se toma de los libres; lo
 * liberado va completo a un solo renglón y nunca a uno en $0 (si no hay a dónde, queda el faltante).
 */
export function setAmountRebalanced(
    rows: FixedPaymentRows,
    key: RowKey,
    requestedCents: number,
    totalCents: number,
): FixedPaymentRows {
    const free = ROW_KEYS.filter((k) => k !== key && canAbsorb(rows, k));
    const freeCents = free.reduce((sum, k) => sum + rows[k].amountCents, 0);
    const current = rows[key].amountCents;
    // Lo que falta asignar se llena primero; después se toma de los libres.
    const missing = Math.max(0, totalCents - rowsSumCents(rows));
    const amount = Math.max(0, Math.min(requestedCents, current + missing + freeCents));
    const next: FixedPaymentRows = { ...rows, [key]: { ...rows[key], amountCents: amount } };
    if (amount === current) return next;
    const delta = amount < current ? amount - current : Math.max(0, amount - current - missing);
    if (delta === 0) return next;
    if (amount < current) {
        const target = releaseTarget(rows, key);
        if (target === null) return next;
        return keepCashOkFor(
            key,
            rows,
            setRowAmount(next, target, next[target].amountCents - delta),
        );
    }
    if (free.length === 1) {
        const k = free[0];
        return keepCashOkFor(key, rows, setRowAmount(next, k, next[k].amountCents - delta));
    }
    const [k0, k1] = free as [RowKey, RowKey];
    const [a, b] = splitDelta(next[k0].amountCents, next[k1].amountCents, delta);
    return keepCashOkFor(key, rows, setRowAmount(setRowAmount(next, k0, a), k1, b));
}

/** Cambia el monto de un renglón; con una sola tarjeta, esa tarjeta lleva el monto del renglón. */
function setRowAmount(rows: FixedPaymentRows, key: RowKey, amountCents: number): FixedPaymentRows {
    if (key !== 'card') {
        return { ...rows, [key]: { ...rows[key], amountCents } };
    }
    const cards =
        rows.card.cards.length === 1 ? [{ ...rows.card.cards[0], amountCents }] : rows.card.cards;
    return { ...rows, card: { ...rows.card, amountCents, cards } };
}

/** Monto escrito en el renglón; con una sola tarjeta también la mueve a ella. */
export function setBlockAmount(
    rows: FixedPaymentRows,
    key: RowKey,
    amountCents: number,
    totalCents: number,
): FixedPaymentRows {
    const next = setAmountRebalanced(rows, key, amountCents, totalCents);
    if (next === rows || key !== 'card') return next;
    return setRowAmount(next, 'card', next.card.amountCents);
}

/** «Todo aquí»: el total va a ese renglón, que queda con candado; los otros en $0 y libres. */
export function assignAll(
    rows: FixedPaymentRows,
    key: RowKey,
    totalCents: number,
): FixedPaymentRows {
    const first = rows.card.cards[0] ?? newCard('card-1');
    const cardCents = key === 'card' ? totalCents : 0;
    return {
        cash: {
            ...rows.cash,
            amountCents: key === 'cash' ? totalCents : 0,
            cashOk: key === 'cash' && rows.cash.cashOk && rows.cash.amountCents === totalCents,
            locked: key === 'cash',
        },
        card: {
            ...rows.card,
            amountCents: cardCents,
            locked: key === 'card',
            cards: [{ ...first, amountCents: cardCents, locked: false }],
        },
        spei: {
            ...rows.spei,
            amountCents: key === 'spei' ? totalCents : 0,
            locked: key === 'spei',
        },
    };
}

/** Un solo candado a la vez: con dos, el tercero ya no tendría qué ajustar. */
export function toggleLock(rows: FixedPaymentRows, key: RowKey): FixedPaymentRows {
    const willLock = !rows[key].locked;
    return {
        cash: { ...rows.cash, locked: willLock && key === 'cash' },
        card: { ...rows.card, locked: willLock && key === 'card' },
        spei: { ...rows.spei, locked: willLock && key === 'spei' },
    };
}

/** Agrega una tarjeta en $0 si todavía cabe. */
export function addCard(rows: FixedPaymentRows, id: string): FixedPaymentRows {
    if (rows.card.cards.length >= maxCards(rows)) return rows;
    return { ...rows, card: { ...rows.card, cards: [...rows.card.cards, newCard(id)] } };
}

/** Quita una tarjeta (siempre queda una); su monto lo absorben los otros renglones libres. */
export function removeCard(
    rows: FixedPaymentRows,
    id: string,
    totalCents: number,
): FixedPaymentRows {
    if (rows.card.cards.length <= 1) return rows;
    const cards = rows.card.cards.filter((c) => c.id !== id);
    const withCards = { ...rows, card: { ...rows.card, cards } };
    const cardCents = cardsSum(cards);
    if (cardCents === rows.card.amountCents) return withCards;
    // Su monto va a donde indica el reparto; sin a dónde, queda el faltante para el vendedor.
    return setAmountRebalanced(withCards, 'card', cardCents, totalCents);
}

/** Candado de una tarjeta, uno a la vez; solo cuenta para el reparto entre tarjetas. */
export function toggleCardLock(rows: FixedPaymentRows, cardId: string): FixedPaymentRows {
    const target = rows.card.cards.find((c) => c.id === cardId);
    if (!target) return rows;
    const willLock = !target.locked;
    return {
        ...rows,
        card: {
            ...rows.card,
            cards: rows.card.cards.map((c) => ({ ...c, locked: willLock && c.id === cardId })),
        },
    };
}

/** Todo el monto del renglón Tarjeta a una tarjeta, con candado; las otras en $0. */
export function assignAllToCard(rows: FixedPaymentRows, cardId: string): FixedPaymentRows {
    const total = rows.card.amountCents;
    return {
        ...rows,
        card: {
            ...rows.card,
            cards: rows.card.cards.map((c) => ({
                ...c,
                amountCents: c.id === cardId ? total : 0,
                locked: c.id === cardId,
            })),
        },
    };
}

/**
 * Cambia una tarjeta. Con una sola, su monto mueve el renglón y reparte contra efectivo y
 * transferencia; con varias, se reparte solo entre las otras tarjetas libres.
 */
export function updateCard(
    rows: FixedPaymentRows,
    id: string,
    patch: Partial<Omit<CardEntry, 'id' | 'locked'>>,
    totalCents: number,
): FixedPaymentRows {
    const cards = rows.card.cards;
    const index = cards.findIndex((c) => c.id === id);
    if (index === -1) return rows;
    const { amountCents, ...receipt } = patch;
    const withReceipt: FixedPaymentRows = {
        ...rows,
        card: {
            ...rows.card,
            cards: cards.map((c) => (c.id === id ? { ...c, ...receipt } : c)),
        },
    };
    if (amountCents === undefined || amountCents === cards[index].amountCents) return withReceipt;

    if (cards.length === 1) {
        return setBlockAmount(withReceipt, 'card', amountCents, totalCents);
    }

    const blockCents = rows.card.amountCents;
    const free = cards.map((_, i) => i).filter((i) => i !== index && !cards[i].locked);
    if (free.length === 0) return withReceipt;
    const lockedOthers = cards
        .filter((c, i) => i !== index && c.locked)
        .reduce((sum, c) => sum + c.amountCents, 0);
    const amount = Math.max(0, Math.min(amountCents, Math.max(0, blockCents - lockedOthers)));
    const delta = amount - cards[index].amountCents;
    const next = withReceipt.card.cards.map((c, i) =>
        i === index ? { ...c, amountCents: amount } : c,
    );
    if (free.length === 1) {
        const o = free[0];
        next[o] = { ...next[o], amountCents: Math.max(0, next[o].amountCents - delta) };
    } else {
        const [i0, i1] = free as [number, number];
        const [a, b] = splitDelta(next[i0].amountCents, next[i1].amountCents, delta);
        next[i0] = { ...next[i0], amountCents: a };
        next[i1] = { ...next[i1], amountCents: b };
    }
    return { ...withReceipt, card: { ...withReceipt.card, cards: next } };
}

/** La tarjeta como renglón de `salePayments`, para validar su comprobante. */
export function cardDraft(card: CardEntry): PaymentDraft {
    return {
        ...createPaymentDraft(card.id, 'CARD'),
        amountCents: card.amountCents,
        cardLast4: card.cardLast4,
        authCode: card.authCode,
    };
}

/** La transferencia como renglón de `salePayments`, para validar su comprobante. */
export function speiDraft(rows: FixedPaymentRows): PaymentDraft {
    return {
        ...createPaymentDraft('spei', 'SPEI'),
        amountCents: rows.spei.amountCents,
        speiKey: rows.spei.speiKey,
        senderClabe: rows.spei.senderClabe,
    };
}

/** Pagos con monto, en el orden del cobro: efectivo, cada tarjeta y transferencia. */
export function rowsToDrafts(rows: FixedPaymentRows): PaymentDraft[] {
    const drafts: PaymentDraft[] = [];
    if (rows.cash.amountCents > 0) {
        drafts.push({
            ...createPaymentDraft('cash', 'CASH'),
            amountCents: rows.cash.amountCents,
            cashOk: rows.cash.cashOk,
        });
    }
    for (const card of rows.card.cards) {
        if (card.amountCents > 0) drafts.push(cardDraft(card));
    }
    if (rows.spei.amountCents > 0) drafts.push(speiDraft(rows));
    return drafts;
}

/** Lo que impide cobrar por la forma del reparto (cuántos pagos y si cuadra); `null` si nada. */
export function rowsShapeBlocker(rows: FixedPaymentRows, totalCents: number): string | null {
    if (totalCents === 0) return null;
    if (rowsToDrafts(rows).length > MAX_PAYMENTS) {
        return 'Un cobro lleva máximo 3 formas de pago';
    }
    const assignedCents = rowsSumCents(rows);
    const missingCents = totalCents - assignedCents;
    const status: PaymentsBalance['status'] =
        missingCents === 0 ? 'MATCH' : missingCents > 0 ? 'SHORT' : 'OVER';
    return balanceBlocker({ assignedCents, missingCents, status });
}

/** Lo primero que impide registrar el cobro; `null` si ya se puede. */
export function rowsBlocker(rows: FixedPaymentRows, totalCents: number): string | null {
    if (totalCents === 0) return null;
    return rowsShapeBlocker(rows, totalCents) ?? paymentsBlocker(rowsToDrafts(rows), totalCents);
}

/** Cobro ya registrado en renglones de solo lectura; la venta vieja sin `payments` es un renglón. */
export function rowsFromRegistered(payment: RegisteredPayment): FixedPaymentRows {
    // Hay sesiones viejas sin `detail`.
    const detail = (payment.detail as RegisteredPayment['detail'] | undefined) ?? {};
    const lines: PaymentLine[] =
        payment.payments ??
        (payment.method
            ? [
                  {
                      method: payment.method,
                      amountCents: payment.amountCents,
                      cashConfirmedInRegister: detail.cashConfirmedInRegister ?? null,
                      cardLast4: detail.cardLast4 ?? null,
                      authCode: detail.authCode ?? null,
                      speiTrackingKey: detail.speiTrackingKey ?? null,
                      senderClabe: detail.senderClabe ?? null,
                  },
              ]
            : []);
    const rows = initialSplits(0);
    const cards: CardEntry[] = [];
    for (const line of lines) {
        if (line.method === 'CASH') {
            rows.cash = {
                ...rows.cash,
                amountCents: rows.cash.amountCents + line.amountCents,
                cashOk: line.cashConfirmedInRegister ?? false,
            };
        } else if (line.method === 'CARD') {
            cards.push({
                ...newCard(`card-${String(cards.length + 1)}`, line.amountCents),
                cardLast4: line.cardLast4 ?? '',
                authCode: line.authCode ?? '',
            });
        } else if (line.method === 'SPEI') {
            rows.spei = {
                ...rows.spei,
                amountCents: rows.spei.amountCents + line.amountCents,
                speiKey: line.speiTrackingKey ?? '',
                senderClabe: line.senderClabe ?? '',
            };
        }
    }
    if (cards.length > 0) rows.card = { ...rows.card, amountCents: cardsSum(cards), cards };
    return rows;
}
